import { useApolloClient, useMutation, useQuery } from '@apollo/client'
import { Box } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'

import {
  Button,
  Container,
  ContentBox,
  Fields,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Row,
  Select,
  Stack,
  Switch,
  Text,
  useAlert,
  validations,
} from '../../components'
import { COUNTRY_LIST, PROVINCES_LIST } from '../../constants'
import { mutations, queries } from '../../graphql'
import { parseAndSetFormErrors } from '../../utils'
import { onlyShowInDev } from './showUnfinishedFeatures'
import { OTHER_PAYMENT_METHODS, SuccessDialog } from './payment/other'

export function DonorInfoStep({
  isLoggedIn,
  me,
  stepperState,
  dispatchStep,
  donationTarget,
  donationTargetId,
  isDonatingToFund,
}) {
  useEffect(() => {
    // eslint-disable-next-line no-console
    if (onlyShowInDev) console.info(`donor info stepperState: ${JSON.stringify(stepperState, null, 2)}`)
  }, [stepperState])
  const client = useApolloClient()
  const [showSuccessDialog, setShowSuccessDialog] = useState(false)

  const { profile } = me ?? {}
  const fundProfile = me?.funds?.find((f) => !f.name?.startsWith('charity-') && !f.closed)?.fundProfile

  const [{ Alert, alertProps }] = useAlert()

  const defaultValues = useMemo(() => {
    if (isLoggedIn) {
      const p = profile
      const fp = fundProfile
      return {
        firstName: p?.firstName ?? '',
        middleInitials: p?.middleInitials ?? '',
        lastName: p?.lastName ?? '',
        phone: p?.phone ?? '',
        corporateName: me?.corporateName ?? '',
        // Always blank - this step creates a brand new fund, never prepopulated
        // from an existing one.
        portalName: '',
        address: {
          lineOne: fp?.address?.lineOne ?? '',
          lineTwo: fp?.address?.lineTwo ?? '',
          city: fp?.address?.city ?? '',
          province: fp?.address?.province ?? '',
          postalCode: fp?.address?.postalCode ?? '',
          country: fp?.address?.country ?? 'Canada',
        },
      }
    }

    const di = stepperState?.donorInfo
    return {
      firstName: di?.firstName ?? '',
      middleInitials: di?.middleInitials ?? '',
      lastName: di?.lastName ?? '',
      phone: di?.phone ?? '',
      corporateName: di?.corporateName ?? '',
      portalName: di?.portalName ?? '',
      address: {
        lineOne: di?.address?.lineOne ?? '',
        lineTwo: di?.address?.lineTwo ?? '',
        city: di?.address?.city ?? '',
        province: di?.address?.province ?? '',
        postalCode: di?.address?.postalCode ?? '',
        country: di?.address?.country ?? 'Canada',
      },
    }
  }, [isLoggedIn, profile, stepperState?.donorInfo])
  const { register, handleSubmit, formState, getValues, setValue, setError, watch, control } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues,
  })
  const { isSubmitting, errors = {} } = formState

  const [updateGuestProfile] = useMutation(mutations.user.updateGuestProfile, {
    onError: (e) => {
      // eslint-disable-next-line no-console
      console.error('Update profile error:', e)
    },
  })

  const [createDonorFund] = useMutation(mutations.funds.createDonorFund)

  const { data: existingGuest } = useQuery(queries.user.getUserByEmail, {
    variables: {
      email: stepperState.email,
    },
  })

  const [guestSignUp] = useMutation(mutations.user.guestSignUp, {
    onError: async (newErrors) => {
      parseAndSetFormErrors(newErrors, setError)
      // eslint-disable-next-line no-console
      console.warn('GuestSignup Failed:', newErrors)
    },
  })

  // @fixme This should be part of the form state
  const [isCorporationEditable, setIsCorporation] = useState(false)
  const isCorporation = isLoggedIn ? me?.isOrganization : isCorporationEditable

  const isOtherPaymentMethod = OTHER_PAYMENT_METHODS.includes(stepperState?.payment?.paymentMethodType)

  const createNewFund = async ({ firstName, middleInitials, lastName, phone, corporateName, portalName, address }) => {
    await client.mutate({
      mutation: mutations.user.updateProfile,
      variables: {
        data: {
          firstName,
          middleInitials,
          lastName,
          phone,
          corporateName,
          address,
        },
      },
      refetchQueries: [{ query: queries.user.me }],
      awaitRefetchQueries: true,
    })

    const { data } = await createDonorFund({
      variables: {
        input: {
          portalName,
          phone: phone || undefined,
          address: address?.lineOne ? address : undefined,
        },
      },
      refetchQueries: [{ query: queries.funds.myFunds }, { query: queries.user.me }],
      awaitRefetchQueries: true,
    })

    return data.createDonorFund
  }

  const onSubmit = async ({
    firstName,
    middleInitials,
    lastName,
    password,
    passwordConfirmation,
    phone,
    corporateName,
    portalName,
    address,
  }) => {
    if (isLoggedIn) {
      const newFund = await createNewFund({
        firstName,
        middleInitials,
        lastName,
        phone,
        corporateName,
        portalName,
        address,
      })

      if (isOtherPaymentMethod) {
        setShowSuccessDialog(true)
        dispatchStep({ state: { activeDonorPortal: newFund } })
      } else {
        dispatchStep({ action: 'NEXT', state: { activeDonorPortal: newFund } })
      }
    } else {
      localStorage.setItem(
        'guest_user_prefill_info',
        JSON.stringify({
          firstName,
          middleInitials,
          lastName,
          email: stepperState.email,
          phone,
          corporateName,
          // /sign-up/signup registers this field as `donorName`, not `portalName`
          donorName: portalName,
          address,
        })
      )

      if (stepperState.isGuest) {
        await updateGuestProfile({
          variables: {
            userId: stepperState.user.id,
            data: {
              firstName,
              middleInitials,
              lastName,
              phone,
              corporateName,
              portalName,
              address,
            },
          },
        })
        dispatchStep({
          //
          action: 'NEXT',
          state: {
            user: {
              firstName,
              lastName,
              middleInitials,
              phone,
              address,
              isOrganization: isCorporation,
              corporateName,
            },
          },
        })
      }
    }
  }

  return (
    <Container maxWidth="md" sx={{ py: 2 }}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <fieldset disabled={isSubmitting} style={{ display: 'contents', border: 0, p: 0, m: 0 }}>
          <Stack spacing={2}>
            <ContentBox border>
              <Stack spacing={2}>
                {!isLoggedIn && (
                  <FormControlLabel
                    size="small"
                    control={<Switch />}
                    checked={isCorporation}
                    onChange={(e) => setIsCorporation(e.target.checked)}
                    label="Sign up as an Organization or Business"
                    sx={{ mb: 2 }}
                  />
                )}
                <Stack alignItems="flex-start">
                  {isCorporation && (
                    <>
                      <Fields.Text
                        inputProps={{ 'data-testid': 'corporateName' }}
                        label="Organization/Business Name *"
                        error={!!errors.corporateName}
                        helperText={errors.corporateName?.message}
                        {...register('corporateName', { ...validations.required })}
                      />
                      {!isLoggedIn && !stepperState.isGuest && (
                        <>
                          <input
                            style={{ display: 'none' }}
                            aria-hidden="true"
                            name="email"
                            autoComplete="username"
                            readOnly
                            value={stepperState.email}
                            required
                          />
                          <Fields.NewPassword register={register} errors={errors} getValues={getValues} />
                        </>
                      )}
                      <Text.Bold>Primary Contact Person</Text.Bold>
                    </>
                  )}
                  {!isCorporation && (
                    <>
                      <Text.Bold>User Details</Text.Bold>
                      <Text.Body>This is the name of the individual who will be managing this account.</Text.Body>
                    </>
                  )}
                  <Fields.Text
                    inputProps={{ 'data-testid': 'firstName' }}
                    label="First Name *"
                    error={!!errors.firstName}
                    helperText={errors.firstName?.message}
                    {...register('firstName', { ...validations.required })}
                  />
                  <Fields.Text
                    inputProps={{ 'data-testid': 'middleInitial' }}
                    label="Middle Initials"
                    error={!!errors.middleInitial}
                    helperText={errors.middleInitial?.message}
                    {...register('middleInitials')}
                  />
                  <Fields.Text
                    inputProps={{ 'data-testid': 'lastName' }}
                    label="Last Name *"
                    error={!!errors.lastName}
                    helperText={errors.lastName?.message}
                    {...register('lastName', { ...validations.required })}
                  />
                  {!isCorporation && !isLoggedIn && !stepperState.isGuest && (
                    <>
                      <input
                        style={{ display: 'none' }}
                        aria-hidden="true"
                        name="email"
                        autoComplete="username"
                        readOnly
                        value={stepperState.email}
                        required
                      />
                      <Fields.NewPassword register={register} errors={errors} getValues={getValues} />
                    </>
                  )}

                  <Text.Bold>Donor Details</Text.Bold>
                  <Text.Body>
                    For compliance purposes, this name must match the source of funds and will appear on all tax
                    receipts.
                  </Text.Body>
                  <Fields.Text
                    inputProps={{ 'data-testid': 'portalName' }}
                    label="Donor Name *"
                    error={!!errors.portalName}
                    helperText={errors.portalName?.message}
                    {...register('portalName', { ...validations.required })}
                  />

                  <Text.Bold>Contact</Text.Bold>
                  <Fields.Text
                    inputProps={{ 'data-testid': 'phoneNumber' }}
                    type="text"
                    label="Phone Number"
                    required
                    error={!!errors.phone}
                    helperText={errors?.phone?.message}
                    {...register('phone', { ...validations.required, ...validations.phoneNumber })}
                  />

                  <Text.Bold>Your Address</Text.Bold>
                  <Text.Body>
                    Address is used for tax receipts only. No physical mail will be sent by GiveWise.
                  </Text.Body>
                  <Fields.Text
                    label="Address Line One"
                    required
                    error={!!errors.address?.lineOne}
                    helperText={errors?.address?.lineOne?.message}
                    {...register('address.lineOne', { ...validations.required })}
                  />
                  <Fields.Text
                    label="Address Line Two"
                    error={!!errors.address?.lineTwo}
                    helperText={errors?.address?.lineTwo?.message}
                    {...register('address.lineTwo')}
                  />
                  <Fields.Text
                    label="City"
                    required
                    error={!!errors.address?.city}
                    helperText={errors?.address?.city?.message}
                    {...register('address.city', { ...validations.required })}
                  />

                  {getValues('address.country') === 'Canada' ? (
                    <FormControl fullWidth>
                      <InputLabel id="provinceLabel">Province *</InputLabel>
                      <Controller
                        control={control}
                        name="address.province"
                        render={({ field: { ref, ...field } }) => (
                          <Select
                            {...field}
                            inputProps={{ 'data-testid': 'province', ref }}
                            label="Province *"
                            labelId="provinceLabel"
                            aria-label="Province *"
                            error={!!errors.address?.province}
                            helpertext={errors?.address?.province?.message}
                            required
                          >
                            {PROVINCES_LIST.map((p) => (
                              <MenuItem key={p} value={p}>
                                {p}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      />
                    </FormControl>
                  ) : (
                    <Controller
                      control={control}
                      name="address.province"
                      render={({ field: { ref, ...field } }) => (
                        <Fields.Text
                          {...field}
                          inputProps={{ 'data-testid': 'province', ref }}
                          label="Province/State"
                          value={watch('address.province')}
                          required
                          error={!!errors.address?.province}
                          helperText={errors?.address?.province?.message}
                        />
                      )}
                    />
                  )}
                  <Fields.Text
                    label="Postal Code"
                    required
                    error={!!errors.address?.postalCode}
                    helperText={errors?.address?.postalCode?.message}
                    {...register('address.postalCode', { ...validations.required })}
                  />
                  <FormControl fullWidth>
                    <InputLabel id="countryLabel">Country *</InputLabel>
                    <Controller
                      name="address.country"
                      control={control}
                      render={({ field: { ref, ...field } }) => (
                        <Select
                          {...field}
                          inputProps={{ 'data-testid': 'country', ref }}
                          onChange={(e) => {
                            field.onChange(e)
                            setValue('address.province', '', { shouldValidate: true })
                          }}
                          label="Country *"
                          labelId="countryLabel"
                          aria-label="Country *"
                          error={!!errors.address?.country}
                          helpertext={errors?.address?.country?.message}
                        >
                          {COUNTRY_LIST.map((c) => (
                            <MenuItem key={c} value={c}>
                              {c}
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    />
                  </FormControl>

                  {!isLoggedIn && (
                    <Row justifyContent="flex-end" spacing={4}>
                      <Text.Body>
                        By clicking{' '}
                        {OTHER_PAYMENT_METHODS.includes(stepperState?.payment?.paymentMethodType)
                          ? 'SUBMIT & CONNECT'
                          : 'NEXT'}{' '}
                        you agree to our{' '}
                        <a
                          href="https://guide.givewise.ca/policy-and-user-documents/terms-and-conditions"
                          target="_blank"
                          rel="noreferrer"
                          data-testid="termConditionLink"
                        >
                          Terms and Conditions
                        </a>{' '}
                        and{' '}
                        <a
                          href="https://guide.givewise.ca/policy-and-user-documents/privacy-policy"
                          target="_blank"
                          rel="noreferrer"
                          data-testid="termConditionLink"
                        >
                          Privacy Policy
                        </a>{' '}
                        and you may receive occasional updates from GiveWise. You can unsubscribe at any time.{' '}
                      </Text.Body>
                      {errors.form && <FormHelperText error={!!errors.form}>{errors.form.message}</FormHelperText>}
                    </Row>
                  )}
                </Stack>

                <Alert {...alertProps} sx={{ alignSelf: 'flex-end' }} />
              </Stack>
            </ContentBox>

            <Stack direction="row" spacing={2}>
              <Button type="button" onClick={() => dispatchStep({ action: 'PREV' })} disabled={isSubmitting}>
                Back
              </Button>
              <Box flexGrow={1} />
              {isOtherPaymentMethod ? (
                <Button
                  type="button"
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting}
                  onClick={async () => {
                    handleSubmit(async (formData) => {
                      if (isLoggedIn) {
                        const newFund = await createNewFund(formData)
                        dispatchStep({ state: { activeDonorPortal: newFund } })
                      } else {
                        localStorage.setItem(
                          'guest_user_prefill_info',
                          JSON.stringify({
                            firstName: formData.firstName,
                            middleInitials: formData.middleInitials,
                            lastName: formData.lastName,
                            email: stepperState.email,
                            phone: formData.phone,
                            corporateName: formData.corporateName,
                            // /sign-up/signup registers this field as `donorName`, not `portalName`
                            donorName: formData.portalName,
                            address: formData.address,
                          })
                        )

                        if (stepperState.isGuest) {
                          await updateGuestProfile({
                            variables: {
                              userId: stepperState.user.id,
                              data: {
                                firstName: formData.firstName,
                                middleInitials: formData.middleInitials,
                                lastName: formData.lastName,
                                phone: formData.phone,
                                portalName: formData.portalName,
                                address: formData.address,
                              },
                            },
                          })
                        }
                      }
                      setShowSuccessDialog(true)
                    })()
                  }}
                >
                  Submit & Connect
                </Button>
              ) : (
                <Button type="submit" disabled={isSubmitting}>
                  {isLoggedIn || stepperState.isGuest ? 'Next' : 'Sign Up'}
                </Button>
              )}
            </Stack>
          </Stack>
        </fieldset>
      </form>
      {showSuccessDialog && (
        <SuccessDialog
          open
          onClose={() => setShowSuccessDialog(false)}
          isLoggedIn={isLoggedIn}
          fundId={isLoggedIn ? stepperState?.activeDonorPortal?.id : stepperState?.fundId}
          selectedOption={stepperState?.payment?.paymentMethodType}
          userEmail={isLoggedIn ? me?.email : stepperState?.email}
          isDonatingToFund={isDonatingToFund}
          donationTargetName={isDonatingToFund ? donationTarget?.name : donationTarget?.accountName}
          donationTargetId={donationTargetId}
        />
      )}
    </Container>
  )
}
